// Stable identifiers for each generated PDF report type.
//
// `ReportTypeId` values are persisted on `report.reportTypeId` and are the
// stable source of truth — DO NOT rename these strings. Display names live
// in `REPORT_TYPE_NAMES` and can be safely renamed (the dropdown / saved
// reports look the new name up via the ID).
//
// `ReportType` (display name) values are also stored in `SurveyTestRoutine.name`
// in each customer's DB. Renaming a value there silently breaks existing
// rows, so prefer keeping them stable too.

export enum ReportTypeId {
  acrMonthlyQC = 'acr_monthly_qc',
  ctAcrMonthlyQC = 'ct_acr_monthly_qc',
  mrAcrMonthlyQC = 'mr_acr_monthly_qc',
  mgMonthlyQC = 'mg_monthly_qc',
  mgAnnualQC = 'mg_annual_qc',
  wsCarMonthlyQC = 'ws_car_monthly_qc',
  mgAwsMonitorQC = 'mg_aws_monitor_qc',
  mgArtefactsTest = 'mg_artefacts_test',
}

export enum ReportType {
  acrMonthlyQC_legacy = 'ACR Monthly QC',
  ctAcrMonthlyQC = 'CT - ACR Monthly QC',
  mrAcrMonthlyQC = 'MR - ACR Monthly QC',
  mgMonthlyQC = 'MG - Monthly QC',
  mgAnnualQC = 'MG - Annual QC',
  wsCarMonthlyQC = 'WS - CAR Monthly QC',
  mgAwsMonitorQC = 'MG - AWS Monitor QC',
  mgArtefactsTest = 'MG - Artefacts Test',
}

// id -> display name lookup. Edit a value here to rename a report; saved
// reports keep their stable id and pick up the new name automatically.
export const REPORT_TYPE_NAMES: Record<ReportTypeId, string> = {
  [ReportTypeId.acrMonthlyQC]: ReportType.acrMonthlyQC_legacy,
  [ReportTypeId.ctAcrMonthlyQC]: ReportType.ctAcrMonthlyQC,
  [ReportTypeId.mrAcrMonthlyQC]: ReportType.mrAcrMonthlyQC,
  [ReportTypeId.mgMonthlyQC]: ReportType.mgMonthlyQC,
  [ReportTypeId.mgAnnualQC]: ReportType.mgAnnualQC,
  [ReportTypeId.wsCarMonthlyQC]: ReportType.wsCarMonthlyQC,
  [ReportTypeId.mgAwsMonitorQC]: ReportType.mgAwsMonitorQC,
  [ReportTypeId.mgArtefactsTest]: ReportType.mgArtefactsTest,
}

// Reverse lookup: display name -> id. Used during generation to derive a
// stable id from the `selectedRoutine` string the frontend sends today.
export const REPORT_TYPE_IDS_BY_NAME: ReadonlyMap<string, ReportTypeId> =
  new Map<string, ReportTypeId>(
    Object.entries(REPORT_TYPE_NAMES).map(
      ([id, name]) => [name, id as ReportTypeId],
    ),
  )

// Reports driven entirely by phantom-test data. They do not require a
// SurveyTest to be associated with the scanner.
export const PHANTOM_DRIVEN_REPORT_TYPES: ReadonlySet<string> = new Set<string>([
  ReportType.mgAwsMonitorQC,
  ReportType.mgArtefactsTest,
])

// ---------------------------------------------------------------------------
// Report types per scanner modality — shared by the client (Reports page
// dropdown, survey-page preset) and the server (report-type resolution) so
// the two cannot drift. Compiled by the server (TS 3.9, es5) and the client
// (TS 4.0): keep to es2017 APIs.
// ---------------------------------------------------------------------------

export type ReportModality = "CT" | "MR" | "MG" | "WS";

/** Report types offered for a scanner modality, in dropdown order. */
export const REPORT_TYPES_BY_MODALITY: Readonly<
  Record<ReportModality, ReportType[]>
> = {
  CT: [ReportType.ctAcrMonthlyQC],
  MR: [ReportType.mrAcrMonthlyQC],
  MG: [
    ReportType.mgMonthlyQC,
    ReportType.mgAnnualQC,
    ReportType.mgAwsMonitorQC,
    ReportType.mgArtefactsTest,
  ],
  WS: [ReportType.wsCarMonthlyQC],
};

/**
 * The survey-driven monthly report a survey test generates by default: the
 * survey page's "Generate Report" preset, and the server's fallback when a
 * client sends no report type.
 */
export const DEFAULT_SURVEY_REPORT_TYPE_BY_MODALITY: Readonly<
  Record<ReportModality, ReportType>
> = {
  CT: ReportType.ctAcrMonthlyQC,
  MR: ReportType.mrAcrMonthlyQC,
  MG: ReportType.mgMonthlyQC,
  WS: ReportType.wsCarMonthlyQC,
};

/** Default period length, in months, per report type. */
export const REPORT_TYPE_PERIOD_MONTHS: Readonly<Record<ReportType, number>> = {
  [ReportType.acrMonthlyQC_legacy]: 1,
  [ReportType.ctAcrMonthlyQC]: 1,
  [ReportType.mrAcrMonthlyQC]: 1,
  [ReportType.mgMonthlyQC]: 1,
  [ReportType.mgAnnualQC]: 12,
  [ReportType.wsCarMonthlyQC]: 1,
  [ReportType.mgAwsMonitorQC]: 1,
  [ReportType.mgArtefactsTest]: 1,
};

const REPORT_TYPE_VALUES: ReadonlySet<string> = new Set<string>(
  Object.values(ReportType)
);

export function isReportType(name: unknown): name is ReportType {
  return typeof name === "string" && REPORT_TYPE_VALUES.has(name);
}

export function isReportModality(
  modality: unknown
): modality is ReportModality {
  return (
    typeof modality === "string" &&
    Object.prototype.hasOwnProperty.call(REPORT_TYPES_BY_MODALITY, modality)
  );
}

/** Reports filled from a survey test, which therefore require one. */
export function isSurveyAnchored(reportType: ReportType): boolean {
  return !PHANTOM_DRIVEN_REPORT_TYPES.has(reportType);
}

export function defaultSurveyReportType(
  modalityId: string | null | undefined
): ReportType | undefined {
  return isReportModality(modalityId)
    ? DEFAULT_SURVEY_REPORT_TYPE_BY_MODALITY[modalityId]
    : undefined;
}

/**
 * Which report to generate.
 *
 * - `selectedRoutine` is the report type the client chose (the Reports page
 *   always sends one). A non-empty value must be a known report type; an
 *   unknown one resolves to `undefined` rather than silently falling back.
 * - Otherwise the survey routine's name, for customers who named a routine
 *   after a report type (the legacy convention; see the header of this file).
 * - Otherwise the modality's default survey-driven report.
 */
export function resolveReportType(args: {
  selectedRoutine?: string | null;
  routineName?: string | null;
  modalityId?: string | null;
}): ReportType | undefined {
  const selected = (args.selectedRoutine || "").trim();
  if (selected) {
    return isReportType(selected) ? selected : undefined;
  }
  const routineName = (args.routineName || "").trim();
  if (isReportType(routineName)) {
    return routineName;
  }
  return defaultSurveyReportType(args.modalityId);
}
